import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();

  const trucks = [
    { width: '280px', height: '100px', bottom: '74px', duration: '12s', delay: '0s' },
    { width: '225px', height: '80px', bottom: '64px', duration: '14s', delay: '-5s' },
    { width: '165px', height: '58px', bottom: '52px', duration: '16s', delay: '-9s' },
    { width: '195px', height: '69px', bottom: '58px', duration: '15s', delay: '-12s' },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-[600px] md:min-h-[700px] lg:min-h-[800px] flex items-center overflow-hidden bg-primary-900"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20white%20delivery%20van%20speeding%20on%20highway%20at%20golden%20hour%20sunset%2C%20motion%20blur%20effect%2C%20warm%20orange%20and%20blue%20sky%2C%20logistics%20transportation%20scene%2C%20cinematic%20photography%2C%20high%20detail&width=1600&height=900&seq=hero-bg-1&orientation=landscape"
          alt="Speed Courier - Transporte de cargas"
          className="w-full h-full object-cover object-top"
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-900/75 to-primary-800/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30" />
      </div>

      {/* Speed streaks - moving light trails */}
      <div className="absolute inset-0 pointer-events-none z-[4]">
        <span className="speed-streak" style={{ top: '18%', left: 0, width: '35%', animationDelay: '0s' }} />
        <span className="speed-streak" style={{ top: '30%', left: 0, width: '28%', animationDelay: '1.2s' }} />
        <span className="speed-streak" style={{ top: '45%', left: 0, width: '40%', animationDelay: '2s' }} />
      </div>

      {/* Moving road + driving truck */}
      <div className="absolute bottom-0 left-0 right-0 h-44 overflow-hidden pointer-events-none z-[6]">
        {/* fade from hero image into the road */}
        <div className="absolute top-0 inset-x-0 h-28 bg-gradient-to-b from-transparent to-foreground-950" />
        {/* road surface */}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-foreground-950" />
        {/* fade road into next section */}
        <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background-50 to-transparent" />
        {/* animated dashed lane line */}
        <div className="absolute inset-x-0 bottom-10 h-1 road-dash opacity-80" />
        {/* driving fleet */}
        {trucks.map((truck, i) => (
          <div
            key={i}
            className="absolute truck-drive"
            style={{ bottom: truck.bottom, animationDuration: truck.duration, animationDelay: truck.delay }}
          >
            <div className="truck-bounce" style={{ animationDelay: `${i * 0.12}s` }}>
              <img
                src="https://readdy.ai/api/search-image?query=Side%20profile%20view%20of%20a%20modern%20white%20delivery%20box%20truck%20with%20a%20large%20cargo%20container%2C%20facing%20right%2C%20isolated%20on%20a%20solid%20dark%20navy%20background%2C%20warm%20golden%20rim%20lighting%20from%20the%20left%2C%20subtle%20motion%20blur%20on%20the%20dark%20wheels%2C%20clean%20minimal%20illustration%20style%20with%20soft%20shading%2C%20high%20detail&width=850&height=300&seq=hero-truck-1&orientation=landscape"
                alt={`Caminhão Speed Courier ${i + 1} em movimento`}
                className="object-cover"
                style={{ width: truck.width, height: truck.height }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-28">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Text content */}
            <div className="flex-1 text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-accent-500/20 border border-accent-500/30 rounded-full px-4 py-1.5 mb-6 animate-slide-up">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-accent-500 text-xs" />
                </div>
                <span className="text-accent-400 text-sm font-medium">{t('hero.badge')}</span>
              </div>

              {/* Title */}
              <h1 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-background-50 leading-tight mb-5 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                Transportando por
                <span className="block text-accent-400">Você.</span>
              </h1>

              {/* Subtitle */}
              <p className="text-base md:text-lg text-background-200/80 max-w-xl mx-auto lg:mx-0 mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                {t('hero.subtitle')}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start animate-slide-up" style={{ animationDelay: '0.3s' }}>
                <a
                  href="#contato"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-6 py-3.5 rounded-md font-semibold text-base transition-colors whitespace-nowrap"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-file-list-3-line" />
                  </div>
                  {t('hero.cta.primary')}
                </a>
                <a
                  href="https://wa.me/552737236099"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-background-50/10 hover:bg-background-50/20 text-background-50 border border-background-300/40 px-6 py-3.5 rounded-md font-semibold text-base transition-colors whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line" />
                  </div>
                  {t('hero.cta.secondary')}
                </a>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 mt-8 justify-center lg:justify-start animate-slide-up" style={{ animationDelay: '0.4s' }}>
                <div className="flex items-center gap-2 text-background-200/70 text-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-accent-400" />
                  </div>
                  <span>Seguro incluso</span>
                </div>
                <div className="flex items-center gap-2 text-background-200/70 text-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line text-accent-400" />
                  </div>
                  <span>Entrega pontual</span>
                </div>
                <div className="flex items-center gap-2 text-background-200/70 text-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-map-pin-line text-accent-400" />
                  </div>
                  <span>Todo o ES</span>
                </div>
              </div>
            </div>

            {/* Right side - placeholder for visual balance, packages float here */}
            <div className="hidden lg:block flex-1 h-[400px]" />
          </div>
        </div>
      </div>
    </section>
  );
}